/**
 * Shared chrome for the /login, /signup, /verify-phone, /forgot-password,
 * /reset-password, and /suspended screens.
 *
 * Renders a centered card with the Pelipost logo on top and a supplied
 * heading + subtitle. Keeps the five auth screens visually consistent
 * without forcing each one to re-implement the layout.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/components/ui/utils';

interface AuthCardProps {
  /** Main heading inside the card. */
  heading: string;
  /** Optional subtitle / description below the heading. */
  subtitle?: ReactNode;
  /** The form or body content. */
  children: ReactNode;
  /** Optional footer (e.g. "Don't have an account? Sign up"). */
  footer?: ReactNode;
  /** Width override — most screens use the default narrow width. */
  width?: 'default' | 'wide';
}

export function AuthCard({
  heading,
  subtitle,
  children,
  footer,
  width = 'default',
}: AuthCardProps) {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="flex items-center justify-center px-6 py-6">
        <Link to="/login" aria-label="Pelipost home">
          <img
            src="/logo-horizontal.png"
            alt="Pelipost"
            className="h-9 w-auto"
          />
        </Link>
      </header>

      <main className="flex flex-1 items-start justify-center px-4 pb-12 sm:items-center sm:px-6">
        <div
          className={cn(
            'w-full rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8',
            width === 'wide' ? 'max-w-xl' : 'max-w-md',
          )}
        >
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
            {subtitle ? (
              <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
          {children}
          {footer ? (
            <div className="mt-6 border-t border-border pt-6 text-center text-sm text-muted-foreground">
              {footer}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
